import { TKey } from '@strength/i18n';
import { DateTime } from 'luxon';

/**
 * The ISO date a workout was dropped on, from the FullCalendar event start
 * (a local-midnight Date for all-day events). `null` when the start is
 * missing — not a resolvable drop.
 */
export function getWorkoutEventDropDateIso(
  start: Date | string | null | undefined,
): string | null {
  if (!start) return null;
  const date =
    typeof start === 'string'
      ? DateTime.fromISO(start)
      : DateTime.fromJSDate(start);
  return date.isValid ? date.toISODate() : null;
}

/**
 * Whether drag interactions are enabled for the calendar's workout events
 * at all: the feature flag plus the Blocks Edit permission (drag matches the
 * manual edit path's permissioning — a coach the builder renders read-only
 * cannot drag).
 *
 * A drag may *start* for every workout while this is true — including
 * workouts whose status blocks the move — so an attempt warns at drag start
 * and snaps back at drop, instead of the attempt failing silently (a
 * non-editable event swallows the drag with no hook to warn from).
 */
export function isWorkoutEventDragEnabled(args: {
  dragInteractionsFlag: boolean;
  canEditBlocks: boolean;
}): boolean {
  return args.dragInteractionsFlag && args.canEditBlocks;
}

/**
 * The status a calendar workout event carries for drag purposes. The
 * published layer's status never says "in progress": a workout with logged
 * actuals keeps status 'planned' on the layer — the builder derives
 * in-progress from actual layers (see workout-statuses.ts), and the
 * calendar mirrors that derivation so a started workout is drag-blocked
 * and a drag attempt warns.
 */
export function deriveWorkoutEventStatus(args: {
  layerStatus: string;
  hasActual: boolean;
}): string {
  if (args.layerStatus === 'planned' && args.hasActual) {
    return 'in-progress';
  }
  return args.layerStatus;
}

/**
 * Whether a workout's status allows a drag to actually move it: only
 * workouts the builder would still allow editing — planned ones, and no
 * started (in-progress) ones, whose logged history belongs to the original
 * date.
 */
export function isWorkoutEventStatusDraggable(status: string): boolean {
  return status === 'planned';
}

/**
 * The i18n key explaining why a workout's status blocks the drag — null
 * when the status allows the move. The drag-start warning explains the
 * reason instead of only saying "can't be moved".
 */
export function getWorkoutEventDragBlockedReasonKey(
  status: string,
): TKey | null {
  if (isWorkoutEventStatusDraggable(status)) return null;
  switch (status) {
    case 'in-progress':
      return 'workoutCannotBeMovedInProgress';
    case 'completed':
      return 'workoutCannotBeMovedCompleted';
    case 'skipped':
      return 'workoutCannotBeMovedSkipped';
    case 'canceled':
      return 'workoutCannotBeMovedCanceled';
    case 'draft':
      return 'workoutCannotBeMovedDraft';
    case 'unpublished-changes':
      return 'workoutCannotBeMovedUnpublishedChanges';
    default:
      return 'workoutCannotBeMoved';
  }
}

/**
 * Whether a drop date stays inside its parent block's start/end dates
 * (inclusive). Date-only ISO strings compare correctly lexicographically.
 */
export function isDropWithinBlockDates(args: {
  blockStartDate: string;
  blockEndDate: string;
  date: string;
}): boolean {
  return args.date >= args.blockStartDate && args.date <= args.blockEndDate;
}
